import { PkPopover } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { FocusEvent } from 'react';
import { memo, useMemo, useRef } from 'react';

import type { HeaderCompetition } from '../../model';
import { handleHeaderItemClick } from '../../utils';

import { useCompetitionLinkRule } from '@hooks/useLinkRule';
import Image from '@shared/Image';
import type { LiveMatch } from '@shared-types/match.types';
import { RoundPhase } from '@shared-types/match.types';
import { imgBaseUrl } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const CompetitionBadge = ({
  competition,
  liveMatches,
}: {
  competition: HeaderCompetition;
  liveMatches: LiveMatch[];
}) => {
  const link = useCompetitionLinkRule(competition.id, competition.phase);

  const linkRef = useRef<HTMLAnchorElement>(null);
  const handleFocus = (e: FocusEvent) => {
    if (e.relatedTarget === linkRef.current) return;
    linkRef.current?.focus();
  };

  const compCode = competition.id === '19' ? 'friendlies' : competition.code;
  const logo = `${imgBaseUrl}/imgml/uefacom/elements/logos/competitions/dark/${compCode}` + '.svg';

  const name = useMemo(() => {
    if (competition.phase === RoundPhase.PREQUALIFYING) return competition.prequalName;
    if (competition.phase === RoundPhase.QUALIFYING) return competition.qualName;

    return competition.name;
  }, [competition]);

  const handleClick = useMemo(() => handleHeaderItemClick({ trackText: competition.intName }), [competition.intName]);

  const isLive = !!liveMatches?.length;

  return (
    <PkPopover
      distance={8}
      placement='bottom'
      showOn='hover'
      skidding={0}
      stroke='none'
      noCloseButton
      className={styles.popover}
      onFocus={handleFocus}
    >
      <a
        onClick={handleClick}
        onAuxClick={handleClick}
        className={clsx('pk-position--relative', isLive && 'pk-mr--xs2')}
        href={link}
        target='_blank'
        aria-label={name}
        slot='trigger'
        ref={linkRef}
      >
        <Image src={logo} width={32} height={32} alt={name} />
        {isLive && (
          <div
            className={clsx(
              'pk-d--flex pk-align-items--center pk-px--xs2 pk-px-sm--xs pk-py-sm--xs3 pk-bg--support-04A',
              styles.badge,
            )}
          >
            <span className='pk-text--text-01 pk-font-size--xs2 pk-line-height--md pk-font-size-md--xs pk-line-height-sm--lg pk-font--base-semibold'>
              {liveMatches.length}
            </span>
          </div>
        )}
      </a>
      <div className='pk-text--text-01 pk-font-size--xs pk-px--xs'>{name}</div>
    </PkPopover>
  );
};

export default memo(CompetitionBadge);
